import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import VideoCard from "@/components/plyx/VideoCard";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Code2 } from "lucide-react";
import CommentDrawer from "@/components/plyx/CommentDrawer";

/**
 * /plyx/v/:videoId — Single-video viewer with **swipe-through gallery**.
 *
 * When a viewer taps a tile in someone's profile gallery, this page
 * loads that creator's full video list and renders a vertical
 * scroll-snap stack starting at the tapped video. Swiping up/down
 * advances through the creator's gallery — same behaviour the user
 * expects from TikTok / IG Reels when entering a profile.
 *
 * If we can't resolve the creator's gallery (e.g. an external embed
 * with no creator handle) we fall back to rendering just the single
 * video so users always see something.
 */
export default function PlyxVideoView() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState(null);   // ordered list — current first
  const [error, setError] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [commentVideo, setCommentVideo] = useState(null);
  const containerRef = useRef(null);

  // Step 1: fetch the requested video. Step 2: try to fetch the creator's
  // gallery and rebuild the list with the target video at the head.
  useEffect(() => {
    let cancelled = false;
    setVideos(null);
    setError(null);
    setActiveIdx(0);

    (async () => {
      try {
        const { data: v } = await plyxApi.get(`/plyx/videos/${videoId}`);
        if (cancelled) return;
        const handle = v?.creator?.handle;
        if (!handle) {
          setVideos([v]);
          return;
        }
        try {
          const { data: prof } = await plyxApi.get(`/plyx/u/${handle}`);
          if (cancelled) return;
          const list = Array.isArray(prof?.videos) ? prof.videos : [];
          // Place the requested video first (start of the swipe stack)
          // and append every other creator video after it. Dedupe by
          // video_id in case the API already includes it at the top.
          const ordered = [v, ...list.filter((x) => x.video_id !== v.video_id)];
          setVideos(ordered);
        } catch {
          if (!cancelled) setVideos([v]);
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        setError(status === 404 ? "Video not found." : "Could not load this video.");
      }
    })();
    return () => { cancelled = true; };
  }, [videoId]);

  // Track active index via IntersectionObserver — same pattern as PlyxFeed
  // so play/pause/auto-mute behave identically.
  useEffect(() => {
    if (!videos || !containerRef.current) return;
    const root = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((ent) => {
          if (ent.isIntersecting && ent.intersectionRatio >= 0.6) {
            const idx = Number(ent.target.getAttribute("data-idx"));
            if (!Number.isNaN(idx)) setActiveIdx(idx);
          }
        });
      },
      { root, threshold: [0.6] },
    );
    root.querySelectorAll("[data-plyx-slide]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [videos]);

  const slides = useMemo(() => videos || [], [videos]);

  if (error) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-black text-white px-6">
        <div className="text-base text-white/80 mb-4" data-testid="plyx-video-view-error">{error}</div>
        <Link to="/plyx" className="px-5 py-2 rounded-full bg-white/10 border border-white/15 text-sm">
          Back to feed
        </Link>
      </div>
    );
  }

  if (!videos) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-black text-white">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black text-white overflow-hidden"
      style={{ height: "100dvh" }}
      data-testid="plyx-video-view"
    >
      <button
        type="button"
        onClick={() => navigate("/plyx/feed")}
        data-testid="plyx-video-view-back"
        aria-label="Back to feed"
        className="fixed top-3 left-3 z-[100] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/70 backdrop-blur border border-white/25 text-white text-sm font-semibold hover:bg-black/85 shadow-lg"
      >
        <ArrowLeft className="w-4 h-4" /> Back to feed
      </button>

      <button
        type="button"
        onClick={async () => {
          const id = slides[activeIdx]?.video_id || videoId;
          const code = `<iframe src="${window.location.origin}/plyx/embed/${id}" width="360" height="640" style="border:0;border-radius:16px;overflow:hidden" allowfullscreen title="Plyx video"></iframe>`;
          try {
            await navigator.clipboard.writeText(code);
            toast.success("Embed code copied — paste it into any website or blog");
          } catch {
            window.prompt("Copy this embed code:", code);
          }
        }}
        data-testid="plyx-video-view-embed"
        aria-label="Copy embed code"
        className="fixed top-[3.4rem] left-3 z-[100] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/70 backdrop-blur border border-white/25 text-white text-xs font-semibold hover:bg-black/85 shadow-lg"
      >
        <Code2 className="w-3.5 h-3.5" /> Embed
      </button>

      {/* Swipe stack — vertical scroll-snap, one full-screen slide per video.
          On a single-video fallback the snap container still works fine; the
          user simply has nothing to swipe to. */}
      <div
        ref={containerRef}
        className="h-full overflow-y-auto snap-y snap-mandatory scrollbar-none"
        style={{ scrollSnapType: "y mandatory" }}
        data-testid="plyx-video-view-stack"
      >
        {slides.map((v, idx) => (
          <div
            key={v.video_id}
            data-plyx-slide
            data-idx={idx}
            className="h-full w-full snap-start snap-always"
          >
            <VideoCard
              video={v}
              isActive={idx === activeIdx}
              isNext={idx === activeIdx + 1}
              onCommentClick={(vv) => setCommentVideo(vv)}
            />
          </div>
        ))}
      </div>

      {commentVideo && (
        <CommentDrawer
          video={commentVideo}
          onClose={() => setCommentVideo(null)}
          onCommentPosted={(vid) => {
            setVideos((vs) => (vs || []).map((v) =>
              v.video_id === vid ? { ...v, comment_count: (v.comment_count || 0) + 1 } : v
            ));
          }}
        />
      )}
    </div>
  );
}
